/**
 * createCoalescingRunner
 *
 * Wraps an async worker so that overlapping invocations are COALESCED rather
 * than dropped. If `schedule()` is called while a run is in flight, exactly one
 * trailing run is queued; any number of further calls during the same run
 * collapse into that single trailing run. The trailing run re-reads whatever
 * state the worker depends on, so the latest input always wins.
 *
 * This replaces the older `if (processing) return` guard in
 * useMaterialCalculations, whose silent-drop behavior let a material quantity
 * computed from a transient mid-typing value (e.g. area "41" → 0.3 tons) persist
 * because the corrective sync (area "41000" → 297 tons) landed while the first
 * sync was still awaiting the backend and was thrown away.
 */
export function createCoalescingRunner(worker: () => Promise<void>): () => void {
  let running = false
  let pending = false

  const drain = async () => {
    running = true
    try {
      do {
        pending = false
        await worker()
      } while (pending)
    } finally {
      running = false
    }
  }

  return function schedule(): void {
    if (running) {
      // A run is in flight — mark that the inputs may have changed so the
      // current drain loop performs one more pass instead of dropping this call.
      pending = true
      return
    }
    // Kick off a drain. Swallow rejection here so a failing run can't wedge the
    // runner or surface an unhandled rejection; the worker owns its own logging.
    void drain().catch(() => {})
  }
}
